/**
 * Lead Visual State Algorithm
 * 
 * This helper manages the visual representation of leads when they are modified.
 * The main function processUpdatedLead coordinates all the logic, determining
 * how leads should be displayed in different filters:
 * 
 * - Gray (outOfFilter): Lead no longer belongs in the current filter
 * - Blue (modifiedButInFilter): Lead was modified but still belongs in the filter
 * - Normal: Lead is unchanged or returned to its original state
 * 
 * The algorithm follows these steps:
 * 1. handleFilterSpecificCases(): Check special cases with predefined visual states
 * 2. isBackToOriginal(): Verify if lead has returned to exactly its original state
 * 3. shouldRemoveHighlight(): Check if highlighting should be removed in certain scenarios
 * 4. shouldMarkAsOutOfFilter(): Determine if lead should be out of filter (gray)
 * 5. shouldHighlightInFilter(): If not out of filter, determine if it should be highlighted (blue)
 * 
 */

import { AllStatusEnum, BillingStatusEnum, MainStatusEnum, NewStatusEnum, PendingStatusEnum, SentStatusEnum, TrashStatusEnum } from '../../../../constants/filterConstants';
import { LeadAction } from '../../../../constants/constants';
import { AdminLeadsCompositeView } from "@common/types/leadsTypes.ts";
import { ModifiedLeadState } from "../../../../context/MainContext.tsx";
import { DisputeStatusEnum } from "@common/types/DisputeTypes.ts";

export type FilterStatus = MainStatusEnum | NewStatusEnum | PendingStatusEnum |
SentStatusEnum | TrashStatusEnum | AllStatusEnum |
DisputeStatusEnum | BillingStatusEnum;

export type OriginalLeadState = {
    buyerLeads: Map<string, { // buyerLeadId -> state
        buyerId: string;
        isConfirmed: boolean;
        sent_date: string | Date | null;
    }>;
    isReassignable: boolean;
};

/**
 * Checks if two dates are equal, useful when changing the send time
 * @param date1 First date to compare
 * @param date2 Second date to compare
 * @returns true if dates are equal, false otherwise
 */
export function isSameSentDate(date1: string | Date | null, date2: string | Date | null): boolean {
    if (date1 === null && date2 === null) return true;
    if (date1 === null || date2 === null) return false;

    const time1 = new Date(date1).getTime();
    const time2 = new Date(date2).getTime();

    // Check if both dates are valid
    if (isNaN(time1) || isNaN(time2)) {
        return false;
    }

    return time1 === time2;
}

/**
 * Determines if a lead should be marked as out of the current filter
 * @param currentFilter Currently applied filter
 * @param action Last action performed in the modal
 * @param originalConfirmed Original confirmation status
 * @param currentConfirmed Current confirmation status
 * @returns true if the lead should be displayed as out of filter (gray)
 */
function shouldMarkAsOutOfFilter(
    currentFilter: FilterStatus,
    action: LeadAction,
    originalConfirmed: boolean = false,
    currentConfirmed: boolean = false
): boolean {
    if (action === LeadAction.CHARGE && currentFilter !== AllStatusEnum.ALL) {
        return true;
    }

    // In confirmed-pending, if it changes from confirmed to assigned, it exits the filter
    if (currentFilter === PendingStatusEnum.CONFIRMED) {
        if (originalConfirmed && !currentConfirmed) {
            return true;
        }
    }

    switch (currentFilter) {
        case NewStatusEnum.ALL:
            // In all-new, only marked as out when confirmed
            return action === LeadAction.CONFIRM;

        case NewStatusEnum.ASSIGNED:
        case PendingStatusEnum.ASSIGNED:
            // In assigned, exits the filter if canceled or confirmed
            return action === LeadAction.CANCEL || action === LeadAction.CONFIRM;

        case PendingStatusEnum.CONFIRMED:
            // In confirmed-pending, exits the filter in multiple cases
            return action === LeadAction.CANCEL ||
                action === LeadAction.CANCEL_CONFIRM ||
                action === LeadAction.CHARGE ||
                action === LeadAction.ASSIGN;

        case PendingStatusEnum.ALL:
            // In pending-all, exits if canceled or charged
            return action === LeadAction.CANCEL || action === LeadAction.CHARGE;

        case NewStatusEnum.NOT_ASSIGNED:
            // In not-assigned, exits if assigned or confirmed
            return action === LeadAction.ASSIGN || action === LeadAction.CONFIRM;

        default:
            return false;
    }
}

/**
 * Determines if a lead should return to its normal visual state
 * @param currentFilter Current filter
 * @param action Last action performed in the modal
 * @param originalBuyerId Original buyer ID
 * @param currentBuyerId Current buyer ID
 * @param originalSentDate Original send date
 * @param currentSentDate Current send date
 * @param originalConfirmed Original confirmation status
 * @param currentConfirmed Current confirmation status
 * @returns true if the lead should return to normal state (without highlighting)
 */
function shouldRemoveHighlight(
    currentFilter: FilterStatus,
    action: LeadAction,
    originalBuyerId: string | null,
    currentBuyerId: string | null,
    originalSentDate: string | Date | null = null,
    currentSentDate: string | Date | null = null,
    originalConfirmed: boolean = false,
    currentConfirmed: boolean = false
): boolean {
    // If we returned to the same buyer with the ASSIGN action
    if (originalBuyerId !== null && currentBuyerId !== null && originalBuyerId === currentBuyerId && action === LeadAction.ASSIGN) {
        // Except in confirmed-pending when confirmation status changes
        return !(currentFilter === PendingStatusEnum.CONFIRMED && originalConfirmed && !currentConfirmed);
    }

    // In confirmed-pending, if we return to the same send date
    if (currentFilter === PendingStatusEnum.CONFIRMED && isSameSentDate(originalSentDate, currentSentDate)) {
        return true;
    }

    switch (currentFilter) {
        case NewStatusEnum.ALL:
        case NewStatusEnum.ASSIGNED:
            return false;

        default:
            return false;
    }
}

/**
 * Determines if a lead should be highlighted as modified but still within the filter
 * @param currentFilter Current filter
 * @param action Last action performed
 * @param originalBuyerId Original buyer ID
 * @param currentBuyerId Current buyer ID
 * @param originalSentDate Original send date
 * @param currentSentDate Current send date
 * @param originalConfirmed Original confirmation status
 * @param currentConfirmed Current confirmation status
 * @returns true if the lead should be highlighted
 */
export function shouldHighlightInFilter(
    currentFilter: FilterStatus,
    action: LeadAction,
    originalBuyerId: string | null,
    currentBuyerId: string | null,
    originalSentDate: string | Date | null = null,
    currentSentDate: string | Date | null = null,
    originalConfirmed: boolean = false,
    currentConfirmed: boolean = false
): boolean {
    // Do not highlight leads that are out of filter (already modified and don't belong to the current filter)
    if (shouldMarkAsOutOfFilter(currentFilter, action, originalConfirmed, currentConfirmed)) {
        return false;
    }

    // If we're in the 'all' filter, highlight any change
    if (currentFilter === AllStatusEnum.ALL || currentFilter === AllStatusEnum.EXCLUDE_TRASH) {
        // Check if there's any change in the assigned buyer
        const buyerChanged = (originalBuyerId !== currentBuyerId) ||
                            (originalBuyerId === null && currentBuyerId !== null) ||
                            (originalBuyerId !== null && currentBuyerId === null);

        // Check if the sent date changed
        const sentDateChanged = !isSameSentDate(originalSentDate, currentSentDate);

        // Check if confirmation status changed
        const confirmationChanged = originalConfirmed !== currentConfirmed;

        // If any of these changed, highlight the lead
        return buyerChanged || sentDateChanged || confirmationChanged ||
               action === LeadAction.CHARGE || action === LeadAction.ASSIGN ||
               action === LeadAction.CANCEL || action === LeadAction.CANCEL_CONFIRM ||
               action === LeadAction.CONFIRM;
    }

    if (currentFilter === NewStatusEnum.ALL) {
        // Highlight when a new or different buyer is assigned
        if (action === LeadAction.ASSIGN &&
            (
                (originalBuyerId === null && currentBuyerId !== null) || // Assign for the first time
                (originalBuyerId !== null && currentBuyerId !== null && originalBuyerId !== currentBuyerId) // change buyer
            )
        ) {
            return true;
        }
        // Highlight when a confirmation is canceled
        if (action === LeadAction.CANCEL_CONFIRM) {
            return true;
        }
        // Highlight when a buyer is canceled
        if (action === LeadAction.CANCEL) {
            return true;
        }
    }

    if (currentFilter === NewStatusEnum.ASSIGNED) {
        // Highlight when changing to a different buyer
        if (action === LeadAction.ASSIGN &&
            originalBuyerId !== null &&
            currentBuyerId !== null &&
            originalBuyerId !== currentBuyerId) {
            return true;
        }
    }

    if (currentFilter === PendingStatusEnum.CONFIRMED) {
        // Highlight when confirmation status changes but it's still the same buyer
        if (originalBuyerId !== null &&
            currentBuyerId !== null &&
            originalBuyerId === currentBuyerId &&
            originalConfirmed === true &&
            currentConfirmed === false
        ) {
            return true;
        }

        // Highlight when send date changes due to confirmation or cancellation
        if ((action === LeadAction.CONFIRM || action === LeadAction.CANCEL_CONFIRM) &&
            !isSameSentDate(originalSentDate, currentSentDate)) {
            return true;
        }
    }

    return false;
}

/**
 * Checks if a lead has returned to its original state to determine if highlighting should be removed
 * @param currentState Current lead state with all buyer_leads
 * @param originalState Original lead state with all buyer_leads
 * @param currentFilter Current filter
 * @returns true if the lead has returned to its original state
 */
export function isBackToOriginal(
    currentState: OriginalLeadState,
    originalState: OriginalLeadState,
    currentFilter: FilterStatus = NewStatusEnum.ALL
): boolean {
    // Check if buyer_leads count changed
    if (currentState.buyerLeads.size !== originalState.buyerLeads.size) {
        return false;
    }

    // If both have no buyer_leads, they're the same
    if (currentState.buyerLeads.size === 0 && originalState.buyerLeads.size === 0) {
        return currentState.isReassignable === originalState.isReassignable;
    }

    // Check each buyer_lead to see if it matches its original state
    for (const [blId, currentBL] of currentState.buyerLeads) {
        const originalBL = originalState.buyerLeads.get(blId);

        // New buyer_lead added
        if (!originalBL) {
            return false;
        }

        // Check if this specific buyer_lead matches
        const sameBuyer = currentBL.buyerId === originalBL.buyerId;
        const sameConfirmation = currentBL.isConfirmed === originalBL.isConfirmed;
        const sameSentDate = isSameSentDate(currentBL.sent_date, originalBL.sent_date);

        // Special case for confirmed-pending when changing from confirmed to not confirmed
        if (currentFilter === PendingStatusEnum.CONFIRMED &&
            originalBL.isConfirmed === true &&
            currentBL.isConfirmed === false) {
            return false;
        }

        // Check equality
        let matches = sameBuyer && sameConfirmation;

        // For confirmed-pending, also check send date
        if (currentFilter === PendingStatusEnum.CONFIRMED) {
            matches = matches && sameSentDate;
        }

        if (!matches) {
            return false;
        }
    }

    // Check if any buyer_leads were removed
    for (const blId of originalState.buyerLeads.keys()) {
        if (!currentState.buyerLeads.has(blId)) {
            return false;
        }
    }

    return currentState.isReassignable === originalState.isReassignable;
}

/**
 * Handles specific filter cases that have particular rules for visual state
 * @param currentFilter Current filter
 * @param lastAction Last action performed
 * @param currentState Current lead state
 * @param originalState Original lead state
 * @returns Visual state to apply or null if not a specific case
 */
function handleFilterSpecificCases(
    leadId: string,
    currentFilter: FilterStatus,
    lastAction: LeadAction,
    currentState: OriginalLeadState,
    originalState: OriginalLeadState
): ModifiedLeadState | null {
    const hadNoBuyerLeads = originalState.buyerLeads.size === 0;
    const hasAnyConfirmed = Array.from(currentState.buyerLeads.values()).some(bl => bl.isConfirmed);

    // Check if any buyer changed
    let anyBuyerChanged = false;
    for (const [blId, currentBL] of currentState.buyerLeads) {
        const originalBL = originalState.buyerLeads.get(blId);
        if (!originalBL || originalBL.buyerId !== currentBL.buyerId) {
            anyBuyerChanged = true;
            break;
        }
    }

    // Case 1: ASSIGN -> CONFIRM -> CANCEL_CONFIRM sequence in all-new
    const isAllNewCancelConfirmSequence =
        currentFilter === NewStatusEnum.ALL &&
        lastAction === LeadAction.CANCEL_CONFIRM &&
        hadNoBuyerLeads;

    if (isAllNewCancelConfirmSequence) {
        return { leadId, isOutOfFilter: false, modifiedButInFilter: true };
    }

    // Case 2: CONFIRM in assigned-new
    const isConfirmInAssignedNew =
        currentFilter === NewStatusEnum.ASSIGNED &&
        lastAction === LeadAction.CONFIRM &&
        hasAnyConfirmed;

    if (isConfirmInAssignedNew) {
        return { leadId, isOutOfFilter: true, modifiedButInFilter: false };
    }

    // Case 3: Assign new buyer in pending-assigned
    const isPendingAssignedWithNewBuyer =
        currentFilter === PendingStatusEnum.ASSIGNED &&
        lastAction === LeadAction.ASSIGN &&
        anyBuyerChanged;

    if (isPendingAssignedWithNewBuyer) {
        return { leadId, isOutOfFilter: false, modifiedButInFilter: true };
    }

    return null;
}

/**
 * Extracts the current state of a lead for comparisons
 * @param lead Lead from which to extract the state
 * @returns Formatted current state with all buyer_leads
 */
export function extractCurrentState(lead: AdminLeadsCompositeView): OriginalLeadState {
    const buyerLeads = new Map(
        lead.buyer_leads.map(bl => [
            bl.id,
            {
                buyerId: bl.user_id,
                isConfirmed: bl.buyer_confirmed ?? false,
                sent_date: bl.sent_date ?? null,
            }
        ])
    );

    return {
        buyerLeads,
        isReassignable: lead.isReassignable,
    };
}

/**
 * Processes a lead with known original state to determine its visual state
 * @param leadToUpdate Lead to process
 * @param originalState Original lead state
 * @param currentFilter Current filter
 * @param lastAction Last action performed
 * @returns the modified state
 */
export function processUpdatedLead(
    leadToUpdate: AdminLeadsCompositeView,
    originalState: OriginalLeadState,
    currentFilter: FilterStatus,
    lastAction: LeadAction
): ModifiedLeadState {
    const currentState = extractCurrentState(leadToUpdate);
    const leadId = leadToUpdate.lead.id;

    // For backward compatibility with existing helper functions,
    // extract first buyer_lead properties if they exist
    const originalFirstBL = Array.from(originalState.buyerLeads.values())[0];
    const currentFirstBL = Array.from(currentState.buyerLeads.values())[0];

    const originalBuyerId = originalFirstBL?.buyerId ?? null;
    const currentBuyerId = currentFirstBL?.buyerId ?? null;
    const originalSentDate = originalFirstBL?.sent_date ?? null;
    const currentSentDate = currentFirstBL?.sent_date ?? null;
    const originalConfirmed = originalFirstBL?.isConfirmed ?? false;
    const currentConfirmed = currentFirstBL?.isConfirmed ?? false;

    // Check specific cases first
    const specialCase = handleFilterSpecificCases(leadId, currentFilter, lastAction, currentState, originalState);
    if (specialCase != null) {
        return specialCase;
    }

    // Check if it has returned to original state
    const backToOriginal = isBackToOriginal(currentState, originalState, currentFilter);

    if (backToOriginal) {
        // If it has returned to original state, remove all visual marks
        return { leadId, modifiedButInFilter: false, isOutOfFilter: false };
    } else if (shouldRemoveHighlight(
        currentFilter,
        lastAction,
        originalBuyerId,
        currentBuyerId,
        originalSentDate,
        currentSentDate,
        originalConfirmed,
        currentConfirmed
    )) {
        // If the action should remove highlighting
        return { leadId, modifiedButInFilter: false, isOutOfFilter: false };
    } else {
        // Apply visual marks as appropriate
        const isOutOfFilter = shouldMarkAsOutOfFilter(
            currentFilter,
            lastAction,
            originalConfirmed,
            currentConfirmed
        );
        const modifiedButInFilter = !isOutOfFilter &&
            shouldHighlightInFilter(
                currentFilter,
                lastAction,
                originalBuyerId,
                currentBuyerId,
                originalSentDate,
                currentSentDate,
                originalConfirmed,
                currentConfirmed
            );
        return {
            leadId,
            modifiedButInFilter,
            isOutOfFilter,
        };
    }
}
